import { useLayoutEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

let nextDialogId = 0;

export default function useUnsavedModalChanges({
  values,
  onClose,
  open = true,
  enabled = true,
  busy = false,
}) {
  const signature = JSON.stringify(values);
  const initial = useRef(null);
  const active = useRef(false);
  const [pending, setPending] = useState(false);
  const [titleId] = useState(() => `unsaved-modal-${++nextDialogId}`);

  useLayoutEffect(() => {
    if (!open || !enabled) {
      active.current = false;
      setPending(false);
    } else if (!active.current) {
      initial.current = signature;
      active.current = true;
    }
  }, [open, enabled, signature]);

  const requestClose = () => {
    if (busy) return;
    if (enabled && active.current && initial.current !== signature) {
      setPending(true);
    } else {
      onClose();
    }
  };

  const dialog = (
    <Dialog
      open={open && pending}
      disableEscapeKeyDown
      aria-labelledby={titleId}
      aria-describedby={`${titleId}-description`}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 11000 }}
      BackdropProps={{ sx: {
        backgroundColor: "var(--overlay-default, rgb(0 0 0 / 45%))",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
      } }}
      PaperProps={{ role: "alertdialog", "aria-modal": true, sx: {
        width: "calc(100% - 32px)",
        margin: "16px",
        maxHeight: "calc(100% - 32px)",
        boxSizing: "border-box",
        borderRadius: "var(--radius-lg, 16px)",
        background: "var(--surface-primary, #fff)",
        color: "var(--text-primary, #142624)",
        border: "1px solid var(--border-subtle, #dce3e2)",
      } }}
    >
      <DialogTitle id={titleId} sx={{
        padding: "var(--space-6, 24px) var(--space-6, 24px) var(--space-2, 8px)",
        margin: 0,
        lineHeight: 1.35,
        overflowWrap: "anywhere",
      }}>
        Discard unsaved changes?
      </DialogTitle>
      <DialogContent sx={{ padding: "0 var(--space-6, 24px) var(--space-6, 24px)" }}>
        <DialogContentText id={`${titleId}-description`} sx={{
          color: "var(--text-secondary, #506360)", margin: 0, lineHeight: 1.5,
        }}>
          Are you sure?
        </DialogContentText>
      </DialogContent>
      <DialogActions disableSpacing sx={{
        padding: "0 var(--space-6, 24px) var(--space-6, 24px)",
        gap: "var(--space-3, 12px)",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "stretch",
        "& .MuiButton-root": {
          height: "var(--control-md, 40px)",
          minHeight: "var(--control-md, 40px)",
          minWidth: { sm: "140px" },
          width: { xs: "100%", sm: "auto" },
          padding: "0 var(--space-4, 16px)",
          margin: 0,
          fontFamily: "var(--font-family-primary, 'Lexend', sans-serif)",
          fontSize: "var(--font-size-label-md, 14px)",
          fontWeight: "var(--font-weight-medium, 500)",
          lineHeight: "var(--line-height-label-md, 1.3)",
          letterSpacing: "var(--letter-spacing-label-md, 0.07px)",
          textTransform: "none",
        },
      }}>
        <Button
          autoFocus
          variant="contained"
          disabled={busy}
          onClick={() => setPending(false)}
          sx={{
            background: "var(--interactive-default, #0f766e)",
            color: "var(--on-interactive-primary, #fff)",
            "&:hover": { background: "var(--interactive-hover, #0a4a46)" },
            "&:focus:not(:hover)": { background: "var(--interactive-default)" },
            "&.Mui-focusVisible": {
              outline: "2px solid var(--focus-ring)",
              outlineOffset: "2px",
            },
            "&:active": { background: "var(--interactive-pressed)" },
          }}
        >
          Continue
        </Button>
        <Button
          variant="outlined"
          disabled={busy}
          sx={{ color: "var(--danger-text, #c23a2b)", borderColor: "var(--border-default, #cdd6d4)" }}
          onClick={() => {
            setPending(false);
            onClose();
          }}
        >
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { requestClose, dialog };
}
